import { useMemo, useState, memo, useCallback } from "react";
import {
  List,
  Icon,
  Input,
  Button,
  ListItem,
  ListIcon,
  ListContent,
  Label,
  Form,
  FormGroup,
  FormField,
  ListList,
  Image,
} from "semantic-ui-react";

// Interface
import { ICONS } from "./sequence/MeForm";

// Utils
import { TreeNode } from "./MeFormType";
import PublishedDiagRuleNodeSerializer from "@/issara-sdk/types/PublishedDiagRuleNodeSerializer_apps_PRX";

type MeFormTreeChildProps = {
  isPublished?: boolean;
  node: TreeNode;
  parentNode?: TreeNode;
  selectedId?: number | null;
  pdrNodeList: PublishedDiagRuleNodeSerializer[];
  hideMap?: Record<number, boolean>;
  level?: number;
  isFirstChild?: boolean;
  isLast: boolean;
  onToggle?: (id: number) => void;
  onSelect?: (node: TreeNode) => void;
  onAddChild?: (parentId: number) => void;
  onAddQuestion?: (id: number) => void;
  onEdit?: (node: TreeNode) => void;
  onRemove?: (id: number) => void;
  onRemoveBranch?: (id: number) => void;
};

const MeFormTreeChild = (props: MeFormTreeChildProps) => {
  const node = useMemo(() => props.node, [props.node]);
  const hideMap = useMemo(() => props.hideMap || {}, [props.hideMap]);

  // --- Edit Question
  const [isEditingText, setIsEditingText] = useState<boolean>(false);
  const [editText, setEditText] = useState<string>(node.text);

  // --- Edit Answer
  const [isEditingCondition, setIsEditingCondition] = useState<boolean>(false);
  const [editCondition, setEditCondition] = useState<string>(node.text);

  const isExpanded = !hideMap[node?.id];
  const isRoot = !!(node.id === 0);
  const isRootChild = !!(node.parent_id === 0);
  const isFirstChildofRoot = isRootChild && node.id === 1;
  const isChild = node.type === "end";
  const hasChildren = !!(node.children && node.children.length > 0);
  const broadcastPostList = props.pdrNodeList.find(pdr => pdr.node_id === node.id)?.broadcast_posts || [];
  const isBroadcasted = broadcastPostList.length > 0;

  const handleSaveQuestion = useCallback(() => {
    setIsEditingText(false);

    if (editText.trim() !== node.text) {
      props.onEdit?.({ ...node, text: editText });
    }
  }, [editText, node, props.onEdit]);

  const handleSaveAnswer = useCallback(() => {
    setIsEditingCondition(false);

    if (editCondition.trim() !== node.condition) {
      props.onEdit?.({ ...node, condition: editCondition });
    }
  }, [editCondition, node, props.onEdit]);

  return (
    <ListItem key={node.id} style={{ padding: 0 }}>
      {/* --- Delete Branch Section --- */}
      {props.isFirstChild && node.parent_id !== null ? (
        <div>
          <div
            style={{
              textAlign: "center",
              paddingBottom: "5px",
              flexGrow: 1,
              width: "2px",
              backgroundColor: "grey",
              marginLeft: "40px",
              ...(isRootChild || props.isPublished ? { height: "1rem" } : { maxHeight: "2px" }),
            }}
          />
          {!isRootChild && !props.isPublished && (
            <>
              <div
                style={{
                  margin: "2px 0px",
                  marginLeft: "33px",
                }}
              >
                <Icon
                  style={{ margin: 0, cursor: "pointer" }}
                  name="close"
                  color="red"
                  onClick={() => {
                    if (props.parentNode) {
                      props.onRemoveBranch?.(props.parentNode?.id);
                    }
                  }}
                />
              </div>
              <div
                style={{
                  textAlign: "center",
                  paddingBottom: "5px",
                  flexGrow: 1,
                  maxHeight: "4px",
                  width: "2px",
                  backgroundColor: "grey",
                  marginLeft: "40px",
                }}
              />
            </>
          )}
        </div>
      ) : null}

      {node.id !== 0 ? (
        <ListIcon>
          <div style={{ display: "flex", height: "100%", gap: "4px", paddingLeft: "8px" }}>
            {props.isLast && !props.isPublished ? (
              <div
                style={{
                  display: "flex",
                }}
              >
                <img
                  width="16px"
                  height="16px"
                  src={ICONS.addNew}
                  style={{ cursor: "pointer", marginTop: "4px" }}
                  onClick={() => props.onAddChild?.(node.parent_id as number)}
                />
              </div>
            ) : (
              <div style={{ width: "16px" }}></div>
            )}

            <div
              style={{
                display: "flex",
                flexGrow: 1,
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <img width="25px" height="25px" src={ICONS.folder} />

              {!props.isLast ? (
                <>
                  <div
                    style={{
                      textAlign: "center",
                      paddingBottom: "5px",
                      flexGrow: 1,
                      width: "2px",
                      backgroundColor: "grey",
                    }}
                  />
                </>
              ) : null}
            </div>

            {hasChildren ? (
              <div>
                <Icon
                  name="angle down"
                  onClick={() => props.onToggle?.(node.id)}
                  style={{
                    marginTop: "5px",
                    // transition: "transform 1s ease",
                    width: "18px",
                    color: "#757575",
                    cursor: "pointer",
                    transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                  }}
                />
              </div>
            ) : null}
          </div>
        </ListIcon>
      ) : null}

      <ListContent
        style={{
          paddingLeft: 0,
          paddingBottom: !isExpanded ? "1rem" : undefined
        }}
      >
        <div style={{ position: "relative", width: "100%", alignItems: "center", display: "flex" }}>
          <Label
            style={{
              display: "inline-block",
              width: "70%",
              height: "100%",
              cursor: "pointer",
              marginBottom: node?.type === "end" ? "0.5rem" : undefined,
              border:
                props.selectedId === node?.id ? "solid 2px var(--primary-theme-color)" : undefined,
            }}
            onClick={() => {
              if (props.selectedId !== node?.id) {
                props.onSelect?.(node);
              }
            }}
          >
            {node?.type === "choices" ? (
              // todo --- แยก Component
              <Form style={{ padding: 0 }}>
                {node?.id !== 0 ? (
                  <FormGroup style={{ margin: 0, marginBottom: "1rem" }} inline>
                    <FormField inline>
                      <div>
                        {isEditingCondition ? (
                          <Input
                            transparent
                            autoFocus
                            value={editCondition}
                            placeholder="Enter option(s)"
                            onChange={(e) => setEditCondition(e.target.value)}
                            onBlur={handleSaveAnswer}
                            onKeyDown={(e: any) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleSaveAnswer();
                              }
                            }}
                            onMouseDown={(e: any) => e.stopPropagation()}
                          />
                        ) : (
                          <label
                            style={{ cursor: "text", fontWeight: "normal" }}
                            onClick={(e) => {
                              if (!props.isPublished) {
                                e.stopPropagation();
                                setEditCondition(node.condition);
                                setIsEditingCondition(true);
                              }
                            }}
                          >
                            {node.condition || "Enter option(s)"}
                          </label>
                        )}
                      </div>
                    </FormField>
                  </FormGroup>
                ) : null}
                <FormGroup inline style={{ margin: 0, justifyContent: "space-between" }}>
                  <FormField inline style={{ display: "flex", alignItems: "center" }}>
                    <img width="25px" height="25px" src={ICONS.question} />
                    {isEditingText ? (
                      <Input
                        transparent
                        autoFocus
                        value={editText}
                        placeholder="Enter new question"
                        onChange={(e) => setEditText(e.target.value)}
                        onBlur={handleSaveQuestion}
                        onKeyDown={(e: any) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSaveQuestion();
                          }
                        }}
                        onMouseDown={(e: any) => e.stopPropagation()}
                      />
                    ) : (
                      <label
                        style={{ cursor: "text", fontWeight: "bold" }}
                        onClick={(e) => {
                          if (!props.isPublished) {
                            e.stopPropagation();
                            setEditText(node.text);
                            setIsEditingText(true);
                          }
                        }}
                      >
                        {node?.text || "Enter new question"}
                      </label>
                    )}
                  </FormField>
                  {!isRoot && !isFirstChildofRoot && !props.isPublished && (
                    <FormField inline style={{ paddingRight: 0 }}>
                      <div>
                        <img
                          style={{ cursor: "pointer" }}
                          width="25px"
                          height="25px"
                          src={ICONS.remove}
                          onClick={() => props.onRemove?.(node?.id)}
                        />
                      </div>
                    </FormField>
                  )}
                </FormGroup>
              </Form>
            ) : (
              <Form style={{ padding: 0 }}>
                <FormGroup style={{ margin: 0, justifyContent: "space-between" }} inline>
                  <FormField inline style={{ display: "flex", alignItems: "center" }}>
                    <div>
                      {isEditingCondition ? (
                        <Input
                          transparent
                          autoFocus
                          value={editCondition}
                          placeholder="Enter option(s)"
                          onChange={(e) => setEditCondition(e.target.value)}
                          onBlur={handleSaveAnswer}
                          onKeyDown={(e: any) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleSaveAnswer();
                            }
                          }}
                          onMouseDown={(e: any) => e.stopPropagation()}
                        />
                      ) : (
                        <label
                          style={{
                            cursor: "text",
                            fontWeight: "normal",
                          }}
                          onClick={(e) => {
                            if (!props.isPublished) {
                              e.stopPropagation();
                              setEditCondition(node.condition);
                              setIsEditingCondition(true);
                            }
                          }}
                        >
                          {node?.condition || "Enter option(s)"}
                        </label>
                      )}
                    </div>
                  </FormField>
                  <FormField
                    inline
                    style={{ display: "flex", justifyContent: "flex-end", paddingRight: 0 }}
                  >
                    {!props.isPublished ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: "4px",
                        }}
                      >
                        <img
                          style={{ cursor: "pointer" }}
                          width="25px"
                          height="25px"
                          src={ICONS.addQuestion}
                          onClick={() => props.onAddQuestion?.(node.id)}
                        />

                        {!isFirstChildofRoot && (
                          <img
                            style={{ cursor: "pointer" }}
                            width="25px"
                            height="25px"
                            src={ICONS.remove}
                            onClick={() => props.onRemove?.(node?.id)}
                          />
                        )}
                      </div>
                    ) : (
                      <div style={{ width: "25px", height: "25px" }}></div>
                    )}
                  </FormField>
                </FormGroup>
              </Form>
            )}
          </Label>

          {props.isPublished && isChild && isBroadcasted && (
            <img
              src={ICONS.bullhorn}
              style={{
                position: "absolute",
                left: "calc(70% + 8px)",
                // top: "calc(50%)",
                pointerEvents: "none",
              }}
            />
          )}
        </div>

        {isExpanded && hasChildren && (
          <ListList style={{ padding: 0, paddingBottom: "1rem" }}>
            {node.children?.map((child, index) => (
              <MeFormTreeChild
                isPublished={props.isPublished}
                key={child.id}
                node={child}
                parentNode={node}
                selectedId={props.selectedId}
                pdrNodeList={props.pdrNodeList}
                hideMap={props.hideMap}
                isFirstChild={index === 0}
                isLast={index === node.children!.length - 1}
                onToggle={props.onToggle}
                onSelect={props.onSelect}
                onAddChild={props.onAddChild}
                onAddQuestion={props.onAddQuestion}
                onEdit={props.onEdit}
                onRemove={props.onRemove}
                onRemoveBranch={props.onRemoveBranch}
              />
            ))}
          </ListList>
        )}
      </ListContent>
    </ListItem>
  );
};

export default memo(MeFormTreeChild);
